package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.WebHistoriqueStrings
import com.milta.feature.route.domain.entity.DeliveryOutcome
import com.milta.feature.route.domain.entity.DeliveryProofHistoryItem
import com.milta.feature.route.domain.entity.FailureReason
import com.milta.feature.route.domain.entity.RouteHistoryItem
import com.milta.feature.route.domain.usecase.GetRouteHistoryUseCase
import com.milta.feature.route.domain.usecase.GetRouteProofsUseCase
import com.milta.feature.route.presentation.HistoriquePeriodMode
import com.milta.feature.route.presentation.WebHistoriqueIntent
import com.milta.feature.route.presentation.WebHistoriqueState
import com.milta.feature.route.presentation.WebHistoriqueViewModel
import com.milta.feature.route.presentation.minSelectableHistoryDateIso
import com.milta.feature.route.presentation.todayIso
import com.milta.feature.team.domain.entity.Driver
import com.milta.feature.team.domain.usecase.GetEnterpriseDriversUseCase
import com.milta.feature.team.domain.usecase.GetMyTeamUseCase
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.koin.compose.koinInject

/**
 * Entry point wired from `Main.kt`'s "Historique des preuves" nav destination (MIL-52). Owns the
 * single [WebHistoriqueViewModel] for BOTH screens of the flow — see [WebHistoriqueState]'s doc for
 * why one ViewModel covers both (Screen A's filters must survive a round trip through Screen B).
 * [WebHistoriqueState.isProofsScreen] is the sole switch rendered here between
 * [HistoryListContent] (Screen A "Historique des tournées") and [ProofsGridContent] (Screen B
 * "Preuves de la tournée").
 *
 * [routeIdFromUrl]/[onSelectedRouteChanged] bridge MIL-52's nested drill-down to `Main.kt`'s browser
 * URL routing (DOM-06) without giving [WebHistoriqueViewModel] itself any `kotlinx.browser`/DOM
 * dependency (it stays platform-agnostic like every other ViewModel here):
 * - [routeIdFromUrl] is `Main.kt`'s current belief of what the URL says (`null` = Screen A,
 *   otherwise the `routeId` segment) — both the initial value on a page load/refresh deep link
 *   AND any later value pushed down after a browser back/forward (`window.onpopstate`). It is
 *   applied synchronously against the freshly-constructed [WebHistoriqueViewModel] (before the
 *   first [collectAsState] read) so a deep link straight onto Screen B never flashes Screen A
 *   first, and reconciled again on every later change via the `LaunchedEffect` below (this second
 *   path is what makes the browser back/forward buttons work while this composable stays mounted,
 *   since [WebHistoriqueState.selectedRouteId] changing doesn't cause a destination switch/remount
 *   in `Main.kt`).
 * - [onSelectedRouteChanged] reports every [WebHistoriqueState.selectedRouteId] change outward
 *   (opening a route AND going back to the list) so `Main.kt` can push the matching nested URL.
 *   `Main.kt` is expected to no-op this callback whenever the new value already matches
 *   [routeIdFromUrl] — i.e. whenever the change was itself caused by the reconciliation above,
 *   rather than by a genuine in-page click — so a browser-driven navigation never gets pushed back
 *   onto the history stack as a brand new entry (which would otherwise leave back/forward "stuck"
 *   toggling between two identical-looking URLs).
 */
@Composable
fun WebHistoriqueContent(
    routeIdFromUrl: String? = null,
    onSelectedRouteChanged: (String?) -> Unit = {},
) {
    val getRouteHistory: GetRouteHistoryUseCase = koinInject()
    val getRouteProofs: GetRouteProofsUseCase = koinInject()
    val getMyTeam: GetMyTeamUseCase = koinInject()
    val getEnterpriseDrivers: GetEnterpriseDriversUseCase = koinInject()
    val viewModel = remember {
        WebHistoriqueViewModel(getRouteHistory, getRouteProofs, getMyTeam, getEnterpriseDrivers).apply {
            // Deep link / page refresh straight onto Screen B — applied synchronously (not via a
            // LaunchedEffect) so the very first `collectAsState()` read below already reflects it,
            // with no transient Screen A frame in between.
            if (routeIdFromUrl != null) onIntent(WebHistoriqueIntent.OpenRouteProofs(routeIdFromUrl))
        }
    }
    val state by viewModel.state.collectAsState()
    val strings = LocalStrings.current.webHistorique
    val routeStrings = LocalStrings.current.route
    val commonStrings = LocalStrings.current.common

    // Browser back/forward while already mounted on this destination (see doc above) — a no-op
    // whenever routeIdFromUrl already matches the current state (in particular right after the
    // synchronous apply{} above, on first composition).
    LaunchedEffect(routeIdFromUrl) {
        if (routeIdFromUrl != state.selectedRouteId) {
            if (routeIdFromUrl != null) viewModel.onIntent(WebHistoriqueIntent.OpenRouteProofs(routeIdFromUrl))
            else viewModel.onIntent(WebHistoriqueIntent.BackToHistory)
        }
    }
    LaunchedEffect(state.selectedRouteId) {
        onSelectedRouteChanged(state.selectedRouteId)
    }

    if (state.isProofsScreen) {
        ProofsGridContent(state = state, strings = strings, routeStrings = routeStrings, offlineMessage = commonStrings.errorOffline, onIntent = viewModel::onIntent)
    } else {
        HistoryListContent(state = state, strings = strings, routeStrings = routeStrings, offlineMessage = commonStrings.errorOffline, onIntent = viewModel::onIntent)
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Screen A — "Historique des tournées"
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun HistoryListContent(
    state: WebHistoriqueState,
    strings: WebHistoriqueStrings,
    routeStrings: com.milta.core.localization.RouteStrings,
    offlineMessage: String,
    onIntent: (WebHistoriqueIntent) -> Unit,
) {
    Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "16px") } }) {
        FiltersCard(state = state, strings = strings, disabled = state.isLoadingHistory, onIntent = onIntent)

        val error = state.historyError
        if (error != null) {
            InlineBanner(kind = InlineBannerKind.Error, text = historyErrorMessage(error, strings, routeStrings, offlineMessage))
        }

        val page = state.historyPage
        when {
            state.isLoadingHistory && page == null -> LoadingPlaceholder("…")
            page != null && page.items.isEmpty() -> EmptyCard(strings.emptyHistoryMessage)
            page != null -> {
                if (state.isLoadingHistory) RefreshingIndicator("…")
                HistoryTable(items = page.items, strings = strings, disabled = state.isLoadingHistory, onIntent = onIntent)
                PaginationControls(
                    currentPage = state.historyPageNumber,
                    totalPages = state.historyTotalPages,
                    strings = strings,
                    disabled = state.isLoadingHistory,
                    onPageChange = { onIntent(WebHistoriqueIntent.GoToHistoryPage(it)) },
                )
            }
        }
    }
}

@Composable
private fun FiltersCard(state: WebHistoriqueState, strings: WebHistoriqueStrings, disabled: Boolean, onIntent: (WebHistoriqueIntent) -> Unit) {
    Div(
        attrs = {
            style {
                property("background", WebSurface); property("border", "1px solid $WebBorder")
                property("border-radius", "12px"); padding(16.px)
                property("display", "flex"); property("flex-wrap", "wrap"); property("gap", "16px")
                alignItems(AlignItems.FlexStart)
            }
        },
    ) {
        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "8px") } }) {
            FilterLabel(strings.periodLabel)
            Div(attrs = { style { property("display", "flex"); property("gap", "8px"); property("flex-wrap", "wrap") } }) {
                PeriodChip(strings.periodLastEightDays, state.periodMode == HistoriquePeriodMode.LastEightDays, disabled = disabled) {
                    onIntent(WebHistoriqueIntent.SelectPeriodMode(HistoriquePeriodMode.LastEightDays))
                }
                PeriodChip(strings.periodSingleDate, state.periodMode == HistoriquePeriodMode.SingleDate, disabled = disabled) {
                    onIntent(WebHistoriqueIntent.SelectPeriodMode(HistoriquePeriodMode.SingleDate))
                }
                PeriodChip(strings.periodRange, state.periodMode == HistoriquePeriodMode.Range, disabled = disabled) {
                    onIntent(WebHistoriqueIntent.SelectPeriodMode(HistoriquePeriodMode.Range))
                }
            }
            when (state.periodMode) {
                HistoriquePeriodMode.SingleDate -> DateField(
                    value = state.singleDate ?: todayIso(),
                    disabled = disabled,
                    onChange = { onIntent(WebHistoriqueIntent.SelectSingleDate(it)) },
                )
                HistoriquePeriodMode.Range -> Div(attrs = { style { property("display", "flex"); property("gap", "8px"); property("align-items", "center") } }) {
                    Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary) } }) { Text(strings.rangeFromLabel) }
                    DateField(value = state.rangeFrom ?: minSelectableHistoryDateIso(), disabled = disabled, onChange = { onIntent(WebHistoriqueIntent.SelectRangeFrom(it)) })
                    Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary) } }) { Text(strings.rangeToLabel) }
                    DateField(value = state.rangeTo ?: todayIso(), disabled = disabled, onChange = { onIntent(WebHistoriqueIntent.SelectRangeTo(it)) })
                }
                HistoriquePeriodMode.LastEightDays -> {}
            }
        }

        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "8px") } }) {
            FilterLabel(strings.driverFilterLabel)
            DriverDropdown(strings = strings, drivers = state.drivers, selectedDriverId = state.selectedDriverId, disabled = disabled, onSelect = { onIntent(WebHistoriqueIntent.SelectDriver(it)) })
        }

        // MIL-86 — free-text search by driver name, combined (AND, debounced server-side in
        // WebHistoriqueViewModel) with the period/driver filters above, never an alternative to them.
        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "8px") } }) {
            FilterLabel(strings.historySearchLabel)
            SearchField(
                value = state.historySearchQuery,
                placeholder = strings.historySearchPlaceholder,
                disabled = disabled,
                onChange = { onIntent(WebHistoriqueIntent.SetHistorySearchQuery(it)) },
            )
        }
    }
}

/** MIL-86 — shared search input for both screens (driver name on Screen A, recipient/address/motif
 * on Screen B). The actual re-fetch is debounced in [WebHistoriqueViewModel] — this dispatches the
 * raw text on every keystroke, same convention as [DateField]/[DriverDropdown] above. */
@Composable
private fun SearchField(value: String, placeholder: String, disabled: Boolean = false, onChange: (String) -> Unit) {
    Input(
        type = InputType.Text,
        attrs = {
            value(value)
            attr("placeholder", placeholder)
            if (disabled) attr("disabled", "true")
            onInput { event -> onChange(event.value) }
            style {
                property("min-width", "220px")
                property("border", "1px solid $WebBorder"); property("border-radius", "9px")
                property("padding", "9px 12px"); property("font-size", "13px")
                property("background", WebSurface); property("color", WebTextPrimary)
                property("opacity", if (disabled) "0.6" else "1")
            }
        },
    )
}

@Composable
private fun FilterLabel(text: String) {
    Div(
        attrs = {
            style {
                property("font-size", "11px"); property("font-weight", "700"); property("letter-spacing", "0.05em")
                property("text-transform", "uppercase"); property("color", WebTextSecondary)
            }
        },
    ) { Text(text) }
}

@Composable
private fun PeriodChip(label: String, selected: Boolean, disabled: Boolean = false, onClick: () -> Unit) {
    Div(
        attrs = {
            if (!disabled) onClick { onClick() }
            style {
                property("background", if (selected) WebActiveTint else WebSurface)
                property("border", "1px solid " + (if (selected) WebTeal else WebBorder))
                property("border-radius", "50px")
                property("padding", "7px 14px"); property("cursor", if (disabled) "not-allowed" else "pointer")
                property("font-size", "13px"); property("font-weight", "700")
                property("color", if (selected) WebPrimary else WebTextSecondary)
                property("white-space", "nowrap"); property("opacity", if (disabled) "0.6" else "1")
            }
        },
    ) { Text(label) }
}

@Composable
private fun DateField(value: String, disabled: Boolean = false, onChange: (String) -> Unit) {
    Input(
        type = InputType.Date,
        attrs = {
            value(value)
            attr("min", minSelectableHistoryDateIso())
            attr("max", todayIso())
            if (disabled) attr("disabled", "true")
            onInput { event -> if (event.value.isNotBlank()) onChange(event.value) }
            style {
                property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                property("padding", "7px 10px"); property("font-size", "13px")
            }
        },
    )
}

@Composable
private fun DriverDropdown(
    strings: WebHistoriqueStrings,
    drivers: List<Driver>,
    selectedDriverId: String?,
    disabled: Boolean = false,
    onSelect: (String?) -> Unit,
) {
    val options = listOf<Pair<String?, String>>(null to strings.driverFilterAllOption) +
        drivers.map { it.id to "${it.firstName} ${it.lastName}" }
    WebNativeSelect(options = options, selectedValue = selectedDriverId, disabled = disabled, minWidth = "220px", onSelect = onSelect)
}

@Composable
private fun HistoryTable(items: List<RouteHistoryItem>, strings: WebHistoriqueStrings, disabled: Boolean, onIntent: (WebHistoriqueIntent) -> Unit) {
    Div(
        attrs = {
            style {
                property("background", WebSurface); property("border", "1px solid $WebBorder")
                property("border-radius", "12px"); property("overflow", "hidden")
                property("opacity", if (disabled) "0.6" else "1")
            }
        },
    ) {
        Table(attrs = { style { width(100.percent); property("border-collapse", "collapse"); property("font-size", "13.5px") } }) {
            Thead {
                Tr {
                    HistoryHeaderCell(strings.columnDate)
                    HistoryHeaderCell(strings.columnDriver)
                    HistoryHeaderCell(strings.columnTotalDeliveries)
                    HistoryHeaderCell(strings.columnDelivered)
                    HistoryHeaderCell(strings.columnFailed)
                    HistoryHeaderCell("")
                }
            }
            Tbody {
                items.forEach { item -> HistoryRow(item = item, strings = strings, disabled = disabled, onIntent = onIntent) }
            }
        }
    }
}

@Composable
private fun HistoryHeaderCell(label: String) {
    Th(
        attrs = {
            style {
                property("text-align", "left"); property("font-size", "10.5px"); property("font-weight", "700")
                property("letter-spacing", "0.05em"); property("text-transform", "uppercase"); property("color", WebTextSecondary)
                padding(10.px, 16.px); property("background", WebSurfaceDim); property("border-bottom", "1px solid $WebBorder")
            }
        },
    ) { Text(label) }
}

@Composable
private fun HistoryRow(item: RouteHistoryItem, strings: WebHistoriqueStrings, disabled: Boolean, onIntent: (WebHistoriqueIntent) -> Unit) {
    val driverName = "${item.driverFirstName} ${item.driverLastName}"
    Tr(
        attrs = {
            if (!disabled) onClick { onIntent(WebHistoriqueIntent.OpenRouteProofs(item.routeId)) }
            style { property("cursor", if (disabled) "default" else "pointer") }
        },
    ) {
        HistoryCell(formatDateDdMmYyyy(item.date))
        Td(attrs = { style { padding(10.px, 16.px); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
            Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "8px") } }) {
                RouteHistoryAvatar(driverName)
                Div(attrs = { style { property("font-weight", "600"); property("color", WebTextPrimary) } }) { Text(driverName) }
            }
        }
        HistoryCell(item.totalDeliveries.toString())
        HistoryCell(item.deliveredCount.toString(), color = WebSuccessInk)
        HistoryCell(item.failedCount.toString(), color = if (item.failedCount > 0) WebErrorInk else WebTextSecondary)
        Td(attrs = { style { padding(10.px, 16.px); property("border-bottom", "1px solid $WebSurfaceDim") } }) {
            Button(
                attrs = {
                    if (disabled) attr("disabled", "true")
                    else onClick { event -> event.stopPropagation(); onIntent(WebHistoriqueIntent.OpenRouteProofs(item.routeId)) }
                    style {
                        property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                        property("background", WebSurface); property("color", WebPrimary)
                        property("font-size", "12.5px"); property("font-weight", "700")
                        property("padding", "7px 12px")
                        property("cursor", if (disabled) "not-allowed" else "pointer"); property("white-space", "nowrap")
                    }
                },
            ) { Text(strings.viewProofsAction) }
        }
    }
}

@Composable
private fun HistoryCell(text: String, color: String = WebTextPrimary) {
    Td(
        attrs = {
            style {
                padding(10.px, 16.px); property("color", color); property("font-variant-numeric", "tabular-nums")
                property("border-bottom", "1px solid $WebSurfaceDim")
            }
        },
    ) { Text(text) }
}

@Composable
private fun RouteHistoryAvatar(name: String) {
    val label = initials(name)
    Div(
        attrs = {
            style {
                width(28.px); height(28.px); property("border-radius", "50%")
                property("background", avatarColorFor(label)); property("display", "flex")
                property("align-items", "center"); property("justify-content", "center"); property("flex-shrink", "0")
                property("color", "#FFFFFF"); property("font-weight", "700"); property("font-size", "11px")
            }
        },
    ) { Text(label) }
}

// ─────────────────────────────────────────────────────────────────────────────
// Screen B — "Preuves de la tournée"
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun ProofsGridContent(
    state: WebHistoriqueState,
    strings: WebHistoriqueStrings,
    routeStrings: com.milta.core.localization.RouteStrings,
    offlineMessage: String,
    onIntent: (WebHistoriqueIntent) -> Unit,
) {
    Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "16px") } }) {
        Div(
            attrs = {
                onClick { onIntent(WebHistoriqueIntent.BackToHistory) }
                style {
                    property("display", "inline-flex"); property("align-items", "center"); property("gap", "6px")
                    property("cursor", "pointer"); property("font-size", "13px"); property("font-weight", "600")
                    property("color", WebPrimary); property("width", "fit-content")
                }
            },
        ) { Text("← ${strings.backToHistoryAction}") }

        val summary = state.proofsResult?.route
        if (summary != null) {
            Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "2px") } }) {
                Div(attrs = { style { property("font-size", "14px"); property("font-weight", "700"); property("color", WebTextPrimary) } }) {
                    Text("${strings.routeOfLabel} ${formatDateDdMmYyyy(summary.date)} · ${summary.driverFirstName} ${summary.driverLastName}")
                }
                Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary) } }) {
                    Text("${summary.totalDeliveries} ${strings.totalDeliveriesLabel} · ${summary.deliveredCount} ${strings.deliveredLabel} · ${summary.failedCount} ${strings.failedLabel}")
                }
            }
        }

        // MIL-86 — free-text search by recipient/address/motif, scoped to this route, debounced
        // server-side in WebHistoriqueViewModel (no client-side "livreur" filter here — implicit,
        // same MIL-52 convention this screen already follows).
        Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "8px") } }) {
            FilterLabel(strings.proofsSearchLabel)
            SearchField(
                value = state.proofsSearchQuery,
                placeholder = strings.proofsSearchPlaceholder,
                disabled = state.isLoadingProofs,
                onChange = { onIntent(WebHistoriqueIntent.SetProofsSearchQuery(it)) },
            )
        }

        val error = state.proofsError
        if (error != null) {
            InlineBanner(kind = InlineBannerKind.Error, text = proofsErrorMessage(error, routeStrings, offlineMessage))
        }

        val proofsPage = state.proofsResult?.proofs
        when {
            state.isLoadingProofs && proofsPage == null -> LoadingPlaceholder("…")
            proofsPage != null && proofsPage.items.isEmpty() -> EmptyCard(strings.emptyProofsMessage)
            proofsPage != null -> {
                if (state.isLoadingProofs) RefreshingIndicator("…")
                val driverName = summary?.let { "${it.driverFirstName} ${it.driverLastName}" }.orEmpty()
                Div(
                    attrs = {
                        style {
                            property("display", "grid"); property("grid-template-columns", "repeat(3, 1fr)")
                            property("gap", "14px")
                            property("opacity", if (state.isLoadingProofs) "0.6" else "1")
                        }
                    },
                ) {
                    proofsPage.items.forEach { proof -> ProofCard(proof = proof, driverName = driverName, strings = strings) }
                }
                PaginationControls(
                    currentPage = state.proofsPageNumber,
                    totalPages = state.proofsTotalPages,
                    strings = strings,
                    disabled = state.isLoadingProofs,
                    onPageChange = { onIntent(WebHistoriqueIntent.GoToProofsPage(it)) },
                )
            }
        }
    }
}

@Composable
private fun ProofCard(proof: DeliveryProofHistoryItem, driverName: String, strings: WebHistoriqueStrings) {
    val isDelivered = proof.status == DeliveryOutcome.Completed

    Div(
        attrs = {
            style {
                property("background", WebSurface); property("border", "1px solid $WebBorder")
                property("border-radius", "12px"); property("overflow", "hidden")
                property("display", "flex"); property("flex-direction", "column")
            }
        },
    ) {
        ProofCardTopZone(proof = proof, isDelivered = isDelivered, strings = strings)
        ProofCardBottomZone(proof = proof, driverName = driverName, isDelivered = isDelivered, strings = strings)
    }
}

/** Tinted proof zone — photo or camera-icon placeholder, date/time badge top-left, status badge
 * top-right. The tint applies even with no photo (spec requirement). */
@Composable
private fun ProofCardTopZone(proof: DeliveryProofHistoryItem, isDelivered: Boolean, strings: WebHistoriqueStrings) {
    val tintBg = if (isDelivered) WebSuccessBg else WebErrorBg
    val badgeInk = if (isDelivered) WebSuccessInk else WebErrorInk
    val badgeLabel = if (isDelivered) strings.statusDeliveredBadge else strings.statusFailedBadge

    Div(
        attrs = {
            style {
                position(Position.Relative); property("background", tintBg)
                height(150.px); property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
            }
        },
    ) {
        val photoUrl = proof.proofPhotoUrl
        var photoLoadFailed by remember(photoUrl) { mutableStateOf(false) }
        if (photoUrl != null && !photoLoadFailed) {
            Img(
                src = photoUrl,
                alt = strings.photoAlt,
                attrs = {
                    style { width(100.percent); height(100.percent); property("object-fit", "cover") }
                    addEventListener("error") { photoLoadFailed = true }
                },
            )
        } else if (photoUrl == null) {
            // Retour 27/10 (point 3) — a stop of a driver's own route validated without photo.
            Div(
                attrs = { style { property("font-size", "12.5px"); property("font-weight", "600"); property("color", badgeInk) } },
            ) { Text(LocalStrings.current.route.proofWithoutPhotoLabel) }
        } else {
            Div(
                attrs = {
                    attr("role", "img")
                    attr("aria-label", strings.photoPlaceholderAlt)
                    style { property("font-size", "30px"); property("opacity", "0.4") }
                },
            ) { Text("📷") }
        }
        Div(
            attrs = {
                style {
                    position(Position.Absolute); property("top", "8px"); property("left", "8px")
                    property("background", "rgba(30,58,74,0.72)"); property("color", "#FFFFFF")
                    property("border-radius", "6px"); property("padding", "3px 7px")
                    property("font-size", "11px"); property("font-weight", "600")
                }
            },
        ) { Text(formatDateTimeBadge(proof.completedAt)) }
        Div(
            attrs = {
                style {
                    position(Position.Absolute); property("top", "8px"); property("right", "8px")
                    property("background", tintBg); property("color", badgeInk)
                    property("border-radius", "50px"); property("padding", "3px 10px")
                    property("font-size", "11px"); property("font-weight", "700")
                }
            },
        ) { Text(badgeLabel) }
    }
}

/** White bottom zone — driver avatar + name + delivery address, plus the "Voir le motif" inline
 * disclosure for [DeliveryOutcome.Failed] cards. */
@Composable
private fun ProofCardBottomZone(proof: DeliveryProofHistoryItem, driverName: String, isDelivered: Boolean, strings: WebHistoriqueStrings) {
    Div(
        attrs = {
            style {
                property("background", WebSurface); padding(12.px, 14.px)
                property("display", "flex"); property("flex-direction", "column"); property("gap", "6px")
            }
        },
    ) {
        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "8px") } }) {
            RouteHistoryAvatar(driverName)
            Div(attrs = { style { property("font-size", "12.5px"); property("font-weight", "600"); property("color", WebTextPrimary) } }) {
                Text(driverName)
            }
        }
        Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary) } }) { Text(proof.address) }

        if (!isDelivered) {
            FailureReasonDisclosure(genericReason = proof.failureReason, reason = proof.comment, strings = strings)
        }
    }
}

/** "Voir le motif" toggle, expanding [reason] in place — purely local UI state (like
 * [WebSettingsScreen]'s `LanguageDropdown` `isOpen`), no network/business logic involved. */
@Composable
private fun FailureReasonDisclosure(genericReason: FailureReason?, reason: String?, strings: WebHistoriqueStrings) {
    if (genericReason == null && reason.isNullOrBlank()) return
    var showReason by remember { mutableStateOf(false) }
    Div(
        attrs = {
            onClick { showReason = !showReason }
            style {
                property("cursor", "pointer"); property("font-size", "12px"); property("font-weight", "700")
                property("color", WebPrimary); property("width", "fit-content")
            }
        },
    ) { Text(if (showReason) strings.hideReasonAction else strings.viewReasonAction) }
    if (showReason) {
        Div(
            attrs = {
                style {
                    property("background", WebSurfaceDim); property("border-radius", "8px")
                    padding(8.px, 10.px); property("font-size", "12px"); property("color", WebTextPrimary)
                }
            },
        ) {
            if (genericReason == FailureReason.RouteClosedByDriver) {
                Div(attrs = { style { property("font-weight", "600") } }) { Text(LocalStrings.current.route.failureReasonRouteClosedByDriver) }
            }
            if (!reason.isNullOrBlank()) Div { Text(reason) }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared — pagination, empty/loading placeholders, error mapping, date formatting
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun PaginationControls(currentPage: Int, totalPages: Int, strings: WebHistoriqueStrings, disabled: Boolean = false, onPageChange: (Int) -> Unit) {
    if (totalPages <= 1) return
    Div(
        attrs = {
            style {
                property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                property("gap", "6px"); property("flex-wrap", "wrap"); property("margin-top", "4px")
            }
        },
    ) {
        PaginationButton(label = strings.paginationPrevious, enabled = currentPage > 1 && !disabled) { onPageChange(currentPage - 1) }
        for (page in 1..totalPages) {
            PaginationPageButton(page = page, isSelected = page == currentPage, enabled = !disabled) { onPageChange(page) }
        }
        PaginationButton(label = strings.paginationNext, enabled = currentPage < totalPages && !disabled) { onPageChange(currentPage + 1) }
    }
}

@Composable
private fun PaginationButton(label: String, enabled: Boolean, onClick: () -> Unit) {
    Button(
        attrs = {
            if (enabled) onClick { onClick() }
            style {
                property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                property("background", WebSurface); property("color", if (enabled) WebTextPrimary else WebTextSecondary)
                property("font-size", "12.5px"); property("font-weight", "600")
                property("padding", "7px 12px"); property("cursor", if (enabled) "pointer" else "not-allowed")
                property("opacity", if (enabled) "1" else "0.5")
            }
        },
    ) { Text(label) }
}

@Composable
private fun PaginationPageButton(page: Int, isSelected: Boolean, enabled: Boolean = true, onClick: () -> Unit) {
    Button(
        attrs = {
            if (enabled) onClick { onClick() }
            style {
                property("border", "1px solid " + (if (isSelected) WebTeal else WebBorder)); property("border-radius", "8px")
                property("background", if (isSelected) WebActiveTint else WebSurface)
                property("color", if (isSelected) WebPrimary else WebTextPrimary)
                property("font-size", "12.5px"); property("font-weight", "700")
                property("padding", "7px 11px"); property("min-width", "32px")
                property("cursor", if (enabled) "pointer" else "not-allowed")
                property("opacity", if (enabled) "1" else "0.6")
            }
        },
    ) { Text(page.toString()) }
}

@Composable
private fun EmptyCard(message: String) {
    Div(
        attrs = {
            style {
                property("background", WebSurface); property("border", "1px solid $WebBorder")
                property("border-radius", "12px"); padding(40.px, 24.px)
                property("text-align", "center"); property("font-size", "13px"); property("color", WebTextSecondary)
                property("box-sizing", "border-box")
            }
        },
    ) { Text(message) }
}

@Composable
private fun LoadingPlaceholder(label: String) {
    Div(
        attrs = { style { property("padding", "40px"); property("text-align", "center"); property("color", WebTextSecondary); property("font-size", "13px") } },
    ) { Text(label) }
}

/** Shown ABOVE an already-loaded (stale) list/grid while a refetch is in flight — pagination,
 * filter change, or re-opening a route. [LoadingPlaceholder] only covers the very first load
 * (no data yet); without this, clicking "Suivant"/a filter/a period chip gave no visual feedback
 * at all until the new page silently swapped in. */
@Composable
private fun RefreshingIndicator(label: String) {
    Div(
        attrs = {
            style {
                property("display", "flex"); property("align-items", "center"); property("gap", "8px")
                property("padding", "6px 2px"); property("color", WebTextSecondary); property("font-size", "12.5px")
            }
        },
    ) { Text(label) }
}

/** Mirrors [com.milta.feature.route.presentation.WebHistoriqueViewModel.mapHistoryError]'s codes
 * exactly — reuses [com.milta.core.localization.RouteStrings.errorGeneric] for `"server_error"`
 * rather than duplicating a generic-error string in [WebHistoriqueStrings], same as
 * [proofsErrorMessage] does for Screen B. A real backend 500 must NOT surface as the offline
 * message: `offlineMessage` is reserved for actual `!is ApiException` failures. */
private fun historyErrorMessage(code: String, strings: WebHistoriqueStrings, routeStrings: com.milta.core.localization.RouteStrings, offlineMessage: String): String = when (code) {
    "invalid_period" -> strings.errorInvalidPeriod
    "offline" -> offlineMessage
    else -> routeStrings.errorGeneric
}

/** Mirrors [com.milta.feature.route.presentation.WebHistoriqueViewModel.mapProofsError]'s codes
 * exactly — reuses [com.milta.core.localization.RouteStrings.errorRouteNotFound]/[com.milta.core.localization.RouteStrings.errorForbidden]
 * rather than duplicating them, same as [WebDashboardContent]'s own `routeDetailErrorMessage`. */
private fun proofsErrorMessage(code: String, routeStrings: com.milta.core.localization.RouteStrings, offlineMessage: String): String = when (code) {
    "route_not_found" -> routeStrings.errorRouteNotFound
    "forbidden" -> routeStrings.errorForbidden
    "offline" -> offlineMessage
    else -> routeStrings.errorGeneric
}

/** "2026-08-12" -> "12/08/2026". */
private fun formatDateDdMmYyyy(dateIso: String): String {
    val parts = dateIso.split("-")
    return if (parts.size == 3) "${parts[2]}/${parts[1]}/${parts[0]}" else dateIso
}

/** "2026-08-12T14:07:00+00:00" -> "12/08 · 14:07" (spec's exact badge format). */
private fun formatDateTimeBadge(iso: String): String {
    val datePart = iso.substringBefore('T')
    val timePart = iso.substringAfter('T', missingDelimiterValue = "")
    val dateParts = datePart.split("-")
    val ddMm = if (dateParts.size == 3) "${dateParts[2]}/${dateParts[1]}" else datePart
    val hhmm = if (timePart.length >= 5) timePart.take(5) else null
    return if (hhmm != null) "$ddMm · $hhmm" else ddMm
}
